// src/store/slices/panchayathSubscriptionReportsSlice.js

import {

  createAsyncThunk,

  createSlice,

} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================

// REPORT KEYS

// ============================================================

export const REPORT_TYPES = {

  MASTER: "master",

  MONTHLY_TRACKER: "monthly_tracker",

  SUMMARY: "summary",

  BILLING_RECEIPT: "billing_receipt",

  // Panchayath-wise accounts summary

  PANCHAYATH_ACCOUNTS: "panchayath_accounts",

};

// ============================================================

// ENDPOINTS

// ============================================================

const REPORT_ENDPOINTS = {

  [REPORT_TYPES.MASTER]:

    "/api/reports/panchayath-master-report",

  [REPORT_TYPES.MONTHLY_TRACKER]:

    "/api/reports/panchayath-monthly-tracker",

  [REPORT_TYPES.SUMMARY]:

    "/api/reports/panchayath-subscription-summary",

  [REPORT_TYPES.BILLING_RECEIPT]:

    "/api/reports/panchayath-billing-receipt-report",

  [REPORT_TYPES.PANCHAYATH_ACCOUNTS]:

    "/api/reports/panchayath-accounts-summary",

};

const REPORT_EXPORT_ENDPOINTS = {

  [REPORT_TYPES.MASTER]:

    "/api/reports/panchayath-master-report/export",

  [REPORT_TYPES.MONTHLY_TRACKER]:

    "/api/reports/panchayath-monthly-tracker/export",

  [REPORT_TYPES.SUMMARY]:

    "/api/reports/panchayath-subscription-summary/export",

  [REPORT_TYPES.BILLING_RECEIPT]:

    "/api/reports/panchayath-billing-receipt-report/export",

  [REPORT_TYPES.PANCHAYATH_ACCOUNTS]:

    "/api/reports/panchayath-accounts-summary/export",

};

// ============================================================

// HELPERS

// ============================================================

const safeMessage = (

  error,

  fallback

) =>

  error?.response?.data?.message ||

  error?.message ||

  fallback;

const normalizeReportPayload = (

  response

) => ({

  rows:

    Array.isArray(

      response?.data?.data

    )

      ? response.data.data

      : [],

  meta:

    response?.data?.meta ||

    {},

});

// ============================================================

// QUERY PARAMS

// ============================================================

const buildReportParams = (filters = {}) => {

  const params = {};

  if (filters.page !== undefined && filters.page !== null && filters.page !== "") {

    params.page = Number(filters.page);

  }

  if (

    filters.pageSize !== undefined &&

    filters.pageSize !== null &&

    filters.pageSize !== ""

  ) {

    params.pageSize = Number(filters.pageSize);

  }

  if (filters.search !== undefined && filters.search !== null) {

    params.search = String(filters.search).trim();

  }

  if (filters.pan_id !== undefined && filters.pan_id !== null) {

    params.pan_id = filters.pan_id;

  }

  if (filters.month !== undefined && filters.month !== null) {

    params.month = String(filters.month).trim();

  }

  if (filters.category !== undefined && filters.category !== null) {

    params.category = String(filters.category)

      .trim()

      .toLowerCase();

  }

  if (filters.status !== undefined && filters.status !== null) {

    params.status = String(filters.status)

      .trim()

      .toLowerCase();

  }

  if (

    filters.generated_type !== undefined &&

    filters.generated_type !== null

  ) {

    params.generated_type = String(filters.generated_type)

      .trim()

      .toLowerCase();

  }

  if (filters.from_date !== undefined && filters.from_date !== null) {

    params.from_date = String(filters.from_date).trim();

  }

  if (filters.to_date !== undefined && filters.to_date !== null) {

    params.to_date = String(filters.to_date).trim();

  }

  if (filters.sort_field !== undefined && filters.sort_field !== null) {

    params.sort_field = String(filters.sort_field).trim();

  }

  if (

    filters.sort_direction !== undefined &&

    filters.sort_direction !== null

  ) {

    params.sort_direction = String(filters.sort_direction)

      .trim()

      .toLowerCase();

  }

  return params;

};

// ============================================================

// FETCH ONE REPORT

// ============================================================

export const fetchPanchayathSubscriptionReportThunk =

  createAsyncThunk(

    "panchayathSubscriptionReports/fetch",

    async (

      {

        reportType,

        filters = {},

      },

      {

        rejectWithValue,

      }

    ) => {

      try {

        const endpoint =

          REPORT_ENDPOINTS[

            reportType

          ];

        if (!endpoint) {

          return rejectWithValue(

            "Invalid report type"

          );

        }

        const res =

          await api.get(

            endpoint,

            {

              params: buildReportParams(filters),

            }

          );

        if (

          res.data?.success ===

          false

        ) {

          return rejectWithValue(

            res.data?.message ||

              "Failed to load report"

          );

        }

        return {

          reportType,

          ...normalizeReportPayload(

            res

          ),

        };

      } catch (error) {

        return rejectWithValue(

          safeMessage(

            error,

            "Failed to load report"

          )

        );

      }

    }

  );

// ============================================================

// EXPORT ONE REPORT

// format: excel | pdf

// ============================================================

export const exportPanchayathSubscriptionReportThunk =

  createAsyncThunk(

    "panchayathSubscriptionReports/export",

    async (

      {

        reportType,

        format,

        filters = {},

      },

      {

        rejectWithValue,

      }

    ) => {

      try {

        const endpoint =

          REPORT_EXPORT_ENDPOINTS[

            reportType

          ];

        if (!endpoint) {

          return rejectWithValue(

            "Invalid report type"

          );

        }

        const normalizedFormat =

          String(

            format || ""

          )

            .trim()

            .toLowerCase();

        if (

          ![

            "excel",

            "pdf",

          ].includes(

            normalizedFormat

          )

        ) {

          return rejectWithValue(

            "Invalid export format"

          );

        }

        const res =

          await api.get(

            endpoint,

            {

              params: {

                ...buildReportParams(filters),

                format: normalizedFormat,

              },

              responseType:

                "blob",

            }

          );

        return {

          reportType,

          format:

            normalizedFormat,

          blob:

            res.data,

        };

      } catch (error) {

        let message =

          "Failed to export report";

        try {

          if (

            error?.response

              ?.data instanceof

            Blob

          ) {

            const text =

              await error.response.data.text();

            const parsed =

              JSON.parse(

                text

              );

            message =

              parsed?.message ||

              message;

          } else {

            message =

              safeMessage(

                error,

                message

              );

          }

        } catch {

          message =

            safeMessage(

              error,

              message

            );

        }

        return rejectWithValue(

          message

        );

      }

    }

  );

// ============================================================

// STATE

// ============================================================

const makeEmptyReport = () => ({

  rows: [],

  meta: {},

});

const initialState = {

  [REPORT_TYPES.MASTER]:

    makeEmptyReport(),

  [REPORT_TYPES.MONTHLY_TRACKER]:

    makeEmptyReport(),

  [REPORT_TYPES.SUMMARY]:

    makeEmptyReport(),

  [REPORT_TYPES.BILLING_RECEIPT]:

    makeEmptyReport(),

  [REPORT_TYPES.PANCHAYATH_ACCOUNTS]:

    makeEmptyReport(),

  loading: false,

  exportLoading: false,

  error: null,

};

// ============================================================

// SLICE

// ============================================================

const panchayathSubscriptionReportsSlice =

  createSlice({

    name:

      "panchayathSubscriptionReports",

    initialState,

    reducers: {

      clearPanchayathSubscriptionReportError:

        (state) => {

          state.error =

            null;

        },

      clearPanchayathSubscriptionReport:

        (

          state,

          action

        ) => {

          const reportType =

            action.payload;

          if (

            state[

              reportType

            ]

          ) {

            state[

              reportType

            ] =

              makeEmptyReport();

          }

        },

      clearAllPanchayathSubscriptionReports:

        (state) => {

          state[

            REPORT_TYPES.MASTER

          ] =

            makeEmptyReport();

          state[

            REPORT_TYPES.MONTHLY_TRACKER

          ] =

            makeEmptyReport();

          state[

            REPORT_TYPES.SUMMARY

          ] =

            makeEmptyReport();

          state[

            REPORT_TYPES.BILLING_RECEIPT

          ] =

            makeEmptyReport();

          state[

            REPORT_TYPES.PANCHAYATH_ACCOUNTS

          ] =

            makeEmptyReport();

          state.error =

            null;

        },

    },

    extraReducers:

      (builder) => {

        builder

          .addCase(

            fetchPanchayathSubscriptionReportThunk.pending,

            (state) => {

              state.loading =

                true;

              state.error =

                null;

            }

          )

          .addCase(

            fetchPanchayathSubscriptionReportThunk.fulfilled,

            (

              state,

              action

            ) => {

              state.loading =

                false;

              const {

                reportType,

                rows,

                meta,

              } =

                action.payload;

              state[

                reportType

              ] = {

                rows:

                  rows || [],

                meta:

                  meta || {},

              };

            }

          )

          .addCase(

            fetchPanchayathSubscriptionReportThunk.rejected,

            (

              state,

              action

            ) => {

              state.loading =

                false;

              state.error =

                action.payload ||

                "Failed to load report";

            }

          )

          .addCase(

            exportPanchayathSubscriptionReportThunk.pending,

            (state) => {

              state.exportLoading =

                true;

              state.error =

                null;

            }

          )

          .addCase(

            exportPanchayathSubscriptionReportThunk.fulfilled,

            (state) => {

              state.exportLoading =

                false;

            }

          )

          .addCase(

            exportPanchayathSubscriptionReportThunk.rejected,

            (

              state,

              action

            ) => {

              state.exportLoading =

                false;

              state.error =

                action.payload ||

                "Failed to export report";

            }

          );

      },

  });

export const {

  clearPanchayathSubscriptionReportError,

  clearPanchayathSubscriptionReport,

  clearAllPanchayathSubscriptionReports,

} =

  panchayathSubscriptionReportsSlice.actions;

export default

panchayathSubscriptionReportsSlice.reducer;
